(ns hai.entity.lock
  "The field locks this page holds, and the ones it was refused. A field typed
   into, such as a task's title, is edited in one place at a time
   ([[hai.pathom.locks]] on the platform): opening its editor takes the lock,
   closing it releases it, and a save lands only while it's held.

   The page is the lock's client, named by [[client-uuid]]: a web-app tab or an
   extension sidebar. One person in two places is two clients, so the second
   place is refused like anyone else's, and may take the lock over.

   A field is addressed as `[ident field]`: the entity's ident, such as
   `[:task/uuid uuid]`, and the field's attribute. Each surface calls
   [[init!]] at boot, so the page frees what it holds when it goes away."
  (:require
   [hai.ui.rf-sugar :refer [>e! defevent defevent-fx defsub]]))

(defonce client-uuid
  (random-uuid))

(defn- lock-params
  [[id-attr id] field]
  {id-attr id
   :lock/field field
   :lock/client-uuid client-uuid})

(defn take-form
  "The `lock/take` mutation: taking the lock, or renewing it if this page has
   it. `steal?` takes it over from the user's own other client."
  [ident field steal?]
  (list 'lock/take (cond-> (lock-params ident field)
                     steal? (assoc :lock/steal? true))))

(defn renew-form
  "The `lock/renew` mutation: this page checking that a lock it holds is still
   its own. Refused once the lock has moved to another client, held there or
   let go since."
  [ident field]
  (list 'lock/renew (lock-params ident field)))

(defn release-form
  "The `lock/release` mutation, for a request that saves the field first."
  [ident field]
  (list 'lock/release (lock-params ident field)))

(defn ->refusal
  "A refused take's or save's error body as the field shows it: who holds the
   lock, since when, in which browser and surface, and whether that is the
   user somewhere else — or that the lock is gone. nil for any other failure."
  [body]
  (let [{:keys [type] :as error} (:com.wsscode.pathom3.error/error-data body)]
    (case type
      :lock/held {:holder (:lock/holder error)
                  :taken-at (:lock/taken-at error)
                  :browser (:lock/browser error)
                  :surface (:lock/surface error)
                  :mine? (:lock/mine? error)}
      :lock/lost {:lost? true}
      nil)))

(defn ->field-value
  "The field as saved when its lock was taken, from the take's response
   `body`. This page's copy may be behind it."
  [body]
  (get-in body ['lock/take :lock/field-value]))

(defn released
  "`db` without the lock on `[ident field]`, for an event that releases it
   within a request of its own."
  [db ident field]
  (update db ::held disj [ident field]))

(defn refused
  "`db` after `[ident field]` was refused with `refusal`: the lock isn't held,
   and the field says why."
  [db ident field refusal]
  (-> db
      (released ident field)
      (assoc-in [::refusals [ident field]] refusal)))

(defn held?
  [db ident field]
  (contains? (get db ::held #{}) [ident field]))

(defsub ::held? [db ident field]
  (held? db ident field))

(defsub ::refusal [db ident field]
  (get-in db [::refusals [ident field]]))

;; `:steal?` takes the lock over from the user's own other client.
;; `:on-taken-fx` runs before the field counts as held, `'?body` in it standing
;; for the response, which carries the field as saved ([[->field-value]]).
(defevent-fx ::take
  [{:keys [db]} ident field & [{:keys [steal? on-taken-fx]}]]
  {:db (update db ::refusals dissoc [ident field])
   :resolve {:query [(take-form ident field steal?)]
             :on-success-fx (conj (vec on-taken-fx) [:dispatch [::taken ident field]])
             :on-error-fx [[:dispatch [::take-failed ident field '?body]]]}})

(defevent ::taken [db ident field]
  (update db ::held (fnil conj #{}) [ident field]))

(defevent-fx ::take-failed
  [{:keys [db]} ident field body]
  (if-let [refusal (->refusal body)]
    {:db (refused db ident field refusal)}
    {:fx [[:dispatch [:toast/error {:text "Could not open this for editing. Try again."}]]]}))

(defevent-fx ::release
  [{:keys [db]} ident field]
  {:db (released db ident field)
   :resolve {:query [(release-form ident field)]}})

(defevent ::dismiss-refusal [db ident field]
  (update db ::refusals dissoc [ident field]))

;; The page is going away: whatever it holds is freed in one request, sent so
;; that it outlives the page.
(defevent-fx ::page-hidden
  [{:keys [db]} _]
  (if (seq (::held db))
    {:db (dissoc db ::held)
     :resolve {:query [(list 'lock/release-client {:lock/client-uuid client-uuid})]
               :keepalive true}}
    {}))

(defn init! []
  (js/window.addEventListener "pagehide" #(>e! [::page-hidden])))
